import { plural } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Flag, KeyRound, RotateCcwKey } from "lucide-react";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { Link } from "react-router-dom";

import { HelperTooltip } from "@/components/ui-kit/base/helper-tooltip.tsx";
import { Tooltip } from "@/components/ui-kit/base/tooltip.tsx";

import {
  supportedLanguages,
  userStatuses,
} from "../../../../src/constants/enum-to-text";
import {
  apiDeleteUsersUuid,
  UserPasswordStatus,
  UserStatus,
} from "../../../api";
import { AppModel } from "../../../app/app-model";
import { AddButton } from "../../../components/ui-kit/add-button";
import { Button } from "../../../components/ui-kit/button";
import { resolveEnumLabel } from "../../../components/ui-kit/fields/utils";
import { CellOverflowList } from "../../../components/ui-kit/tables/cells/cell-overflow-list";
import { DataTable } from "../../../components/ui-kit/tables/data-table";
import { InfoTable } from "../../../components/ui-kit/tables/info-table";
import { connectedRoles } from "../../../constants/constants";
import { hasAdminAccess } from "../../../constants/permissions";
import { uniqueValueOptions } from "../../../utils/filter-presets";
import { assert, dateTimeOrNull } from "../../../utils/utils";
import { UserPassStatus } from "../admin-console";
import { AddUserDialog } from "./add-user-dialog";
import { TemporaryPasswordDialog } from "./temporary-password-dialog";
import { UserEditDialog } from "./user-edit-dialog";
import { UserFlagsDialog } from "./user-flags-dialog";
import { UserEditDraft, UsersItem } from "./users";

export const UsersRoute = observer(function UsersRoute(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const admin = props.app.requireAdminShit();
  const model = admin.users;
  assert(model, "Users model not initialized");
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [flagsFor, setFlagsFor] = useState<UsersItem | null>(null);
  const isAdmin = hasAdminAccess(admin.api.permissions);

  return (
    <>
      <TemporaryPasswordDialog admin={admin} />

      {flagsFor && (
        <UserFlagsDialog
          item={flagsFor}
          users={model}
          onClose={() => setFlagsFor(null)}
        />
      )}

      {isAdmin && (
        <>
          {showAddDialog && (
            <AddUserDialog
              admin={admin}
              onClose={() => setShowAddDialog(false)}
              onSaved={() => {
                void admin.refreshUsers();
              }}
            />
          )}
        </>
      )}

      <DataTable
        persistState
        editMode="dialog"
        itemKeyField="uuid"
        filterNoun={(count) =>
          t`${plural(count, { one: "user", other: "users" })}`
        }
        filterActions={
          <AddButton onClick={() => setShowAddDialog(true)}>
            <Trans>New user</Trans>
          </AddButton>
        }
        columns={[
          {
            header: <Trans>Username</Trans>,
            attr: "username",
            type: "string",
            sort: true,
            filter: { kind: "text", group: t`Users`, placeholder: t`Search…` },
            sticky: true,
            cell: ({ item }) => <strong>{item.username}</strong>,
          },
          {
            header: <Trans>Role</Trans>,
            attr: "role",
            type: "string",
            filter: {
              kind: "multi",
              group: t`Users`,
              options: uniqueValueOptions<UsersItem>("role"),
            },
          },
          {
            header: <Trans>Email</Trans>,
            attr: "email",
            type: "string",
            width: 220,
            editable: hasAdminAccess(admin.api.permissions),
            copyable: true,
          },
          {
            header: <Trans>Status</Trans>,
            attr: "status",
            type: "enum",
            filter: {
              kind: "multi",
              group: t`Users`,
              options: new Map(Object.entries(userStatuses)),
            },
            values: userStatuses,
            cell: ({ item }) => (
              <span
                className={
                  item.status === UserStatus.active
                    ? "text-success-foreground"
                    : item.status === UserStatus.not_active
                      ? "text-danger-foreground"
                      : "text-warning-foreground"
                }
              >
                {resolveEnumLabel(userStatuses[item.status])}
                {item.last_requested_reset && (
                  <HelperTooltip
                    className="ml-1"
                    content={t`Password reset requested on: ${dateTimeOrNull(item.last_requested_reset)}`}
                  />
                )}
              </span>
            ),
          },
          {
            header: <Trans>Added</Trans>,
            attr: "created_at",
            type: "datetime",
            sort: true,
            initSort: "desc",
          },
          {
            header: <Trans>Added by</Trans>,
            attr: "added_by",
            type: "enum",
            values: model.userMapping,
            filter: {
              kind: "multi",
              group: t`Users`,
              // Read userMapping lazily — it fills in after the users load.
              options: () => new Map(Object.entries(model.userMapping)),
            },
          },
          {
            header: <Trans>Last login</Trans>,
            attr: "last_login",
            type: "datetime",
          },
          {
            header: <Trans>Preferred language</Trans>,
            attr: "preferred_language",
            type: "enum",
            values: supportedLanguages,
            editable: true,
            width: 100,
          },
          {
            header: <Trans>Connected ID</Trans>,
            cell: ({ item }) => (
              <strong>
                <Link
                  className="text-brand-primary"
                  to={`/patient/${item.connected_patient_id}`}
                >
                  {item.connected_patient_id}
                </Link>
              </strong>
            ),
          },
          {
            header: <Trans>Managed IDs</Trans>,
            cell: ({ item }) => (
              <CellOverflowList
                items={(item.visible_patients ?? []).map((p) => (
                  <strong key={p.patient_id}>
                    <Link
                      className="text-brand-primary"
                      to={`/patient/${p.patient_id}`}
                    >
                      {p.patient_id}
                    </Link>
                  </strong>
                ))}
              />
            ),
          },
          {
            header: (
              <span className="flex items-center gap-1">
                <Trans>Reset user</Trans>
                <HelperTooltip
                  content={t`Send 'forgot password' reset email.`}
                />
              </span>
            ),
            cell: ({ item }) =>
              connectedRoles.includes(item.role) && (
                <div>
                  <Button
                    variant="subtle"
                    aria-label={t`Reset user`}
                    onClick={async () => {
                      await admin.resetUser(item.uuid);
                    }}
                  >
                    <RotateCcwKey size={16} className="text-foreground" />
                  </Button>
                </div>
              ),
          },
          {
            // The per-row tooltip stays: it is that user's password status, the
            // only place the value is shown. The header explains the button.
            header: (
              <span className="flex items-center gap-1">
                <Trans>Password state</Trans>
                <HelperTooltip
                  content={t`Issue a one-time temporary password, valid 2 days. Each button's tooltip shows that user's current password state.`}
                />
              </span>
            ),
            cell: ({ item }) =>
              connectedRoles.includes(item.role) && (
                <div>
                  <Tooltip
                    content={
                      item.password_status
                        ? resolveEnumLabel(UserPassStatus[item.password_status])
                        : undefined
                    }
                  >
                    <Button
                      variant="subtle"
                      aria-label={t`Temp. password`}
                      disabled={
                        item.password_status ===
                          UserPasswordStatus.temporary_valid ||
                        item.password_status ===
                          UserPasswordStatus.temporary_error
                      }
                      onClick={async () => {
                        await admin.temporaryPassword(item.uuid);
                      }}
                    >
                      <KeyRound size={16} className="text-foreground" />
                    </Button>
                  </Tooltip>
                </div>
              ),
          },
          // Admin-only, per the spec: a user cannot set even their own flags.
          // The `fe_*` flags apply to staff, who have no other screen to be
          // toggled from - the patient settings page only reaches a patient's
          // own connected user.
          ...(isAdmin
            ? [
                {
                  header: (
                    <span className="flex items-center gap-1">
                      <Trans>Feature flags</Trans>
                      <HelperTooltip
                        content={t`Show and set a user's feature flags.`}
                      />
                    </span>
                  ),
                  cell: ({ item }: { item: UsersItem }) => (
                    <div>
                      <Button
                        variant="subtle"
                        aria-label={t`Feature flags`}
                        onClick={() => setFlagsFor(item)}
                      >
                        <Flag size={16} className="text-foreground" />
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        model={model}
        renderEditDialog={({ item, onClose, onSave }) => (
          <UserEditDialog
            item={item}
            admin={admin}
            onClose={onClose}
            onSave={onSave}
          />
        )}
        // renderEditDialog's onSave routes its patch through onEdit. The patch
        // is a UserEditDraft (Users.prepareDraft normalizes visible_patients to
        // a list of ids), which DataTable surfaces as the generic Partial.
        onEdit={(editedField, item) => {
          const draft = editedField as UserEditDraft;
          return admin.updateUser(item.uuid, {
            preferred_language: draft.preferred_language,
            connected_patient_id: draft.connected_patient_id,
            visible_patient_ids: draft.visible_patients,
            email: draft.email,
          });
        }}
        onDelete={async (item) => {
          await apiDeleteUsersUuid({ path: { uuid: item.uuid } });
          await admin.refreshUsers();
        }}
      />

      <div className="flex flex-col items-start">
        <h4 className="mt-12 mb-2 font-bold">
          <Trans>User roles:</Trans>
        </h4>
        <InfoTable userClass>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>self-only</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>patient-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and only sees its
                <em>connected patient ID</em>. This is typically user for app
                that has patient at home.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>physician-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and can see its
                <em>managed patient IDs</em>. This can be app that some
                physician might have at their office so they can measure their
                patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 1</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>study-ppg-app</strong>
            </td>
            <td>
              <Trans>
                Only create PPG data and can see <em>all patient IDs</em>. This
                is SmartCare app at Premedix Office, so we can measure any
                patient.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 2</strong> | <em>self-only</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>patient</strong>
            </td>
            <td>
              <Trans>
                Limited read/write for any data, can update data they created.
                Only sees its <em>connected patient ID</em>. This is user for
                Seerlinq phone app for submitting basic patient and medial data.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 2</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>physician</strong>
            </td>
            <td>
              <Trans>
                Limited read/write for any data, can update data they created.
                It can see their <em>managed patient IDs</em>. This is user for
                any physician that monitors their patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 3</strong> | <em>managed</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>study-physician</strong>
            </td>
            <td>
              <Trans>
                Full read/write for any data, can update data they created. It
                can see their <em>managed patient IDs</em>. This is our
                related-physician (think Amitai) that can see all and any data,
                run algorithms, but still sees only their patients.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 3</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>seerlinq-user</strong>
            </td>
            <td>
              <Trans>
                Full read/write for any data, can update data they created. It
                can see
                <em>all patient IDs</em>. This is for internal Seerlinq people
                for full access to the DB.
              </Trans>
            </td>
          </tr>
          <tr>
            <td>
              <Trans>
                <strong>Level 4</strong> | <em>full</em>
              </Trans>{" "}
            </td>
            <td>
              <strong>admin</strong>
            </td>
            <td>
              <Trans>
                God of the API and DB. Can delete any data. Only admin can
                create new users.
              </Trans>
            </td>
          </tr>
        </InfoTable>
      </div>
      <br className="h-5" />
    </>
  );
});
